import { createTheme } from '@mui/material/styles';

// Define base colors for reuse - Light Theme
const colors = {
  primaryText: 'rgba(0, 0, 0, 0.87)', // Dark text for light background
  secondaryText: 'rgba(0, 0, 0, 0.6)',
  lightBgPrimary: '#FFFFFF', // White main background
  // Adjusted Paper background for glassmorphism
  lightBgPaperGlass: 'rgba(255, 255, 255, 0.7)',
  lightBgAppBar: 'rgba(255, 255, 255, 0.85)', // Semi-transparent white AppBar/Drawer
  subtleBorder: 'rgba(0, 0, 0, 0.12)', // Dark subtle border
  lighterBorder: 'rgba(0, 0, 0, 0.08)', // Even lighter dark border
  accentBlue: '#007AFF', // Vibrant blue accent (works well on light)
  accentPurple: '#AF52DE', // Vibrant purple accent (works well on light)
  hoverBg: 'rgba(0, 0, 0, 0.04)', // Dark overlay on hover
  selectedBg: 'rgba(0, 0, 0, 0.08)', // Darker overlay for selected
  selectedHoverBg: 'rgba(0, 0, 0, 0.12)', // Darker overlay for selected hover
  // Colors for background blur effect
  blurBlue1: 'rgba(50, 150, 255, 0.4)',
  blurBlue2: 'rgba(0, 100, 220, 0.3)',
  blurPurple: 'rgba(150, 100, 255, 0.2)',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.accentBlue,
    },
    secondary: {
      main: colors.accentPurple,
    },
    background: {
      default: colors.lightBgPrimary,
      // Use the new glass paper background by default for Paper components
      paper: colors.lightBgPaperGlass,
    },
    text: {
      primary: colors.primaryText,
      secondary: colors.secondaryText,
    },
  },
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html {
          box-sizing: border-box;
        }
        *,
        *::before,
        *::after {
          box-sizing: inherit;
        }
        body {
           background-color: ${colors.lightBgPrimary}; /* Base background */
           height: 100vh;
           margin: 0;
           overflow: hidden; /* Prevent scrollbars from the pseudo-element */
           position: relative; /* Needed for z-index stacking */
        }
        body::before {
            content: '';
            position: fixed; /* Cover the entire viewport */
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: -1; /* Place it behind all other content */
            /* Multiple radial gradients for blurred circles */
            background-image: 
              radial-gradient(circle at 15% 25%, ${colors.blurBlue1} 0%, transparent 40%),
              radial-gradient(circle at 80% 40%, ${colors.blurBlue2} 0%, transparent 50%),
              radial-gradient(circle at 50% 80%, ${colors.blurPurple} 0%, transparent 45%);
            background-size: 100% 100%;
            background-repeat: no-repeat;
            filter: blur(100px); /* Apply a strong blur */
            opacity: 0.8; /* Adjust opacity as needed */
            transform: scale(1.1); /* Slightly scale up to avoid edge artifacts */
        }
        #root {
          height: 100%;
          position: relative; /* Ensure root is stacked above pseudo-element */
          z-index: 1;
        }
      `,
    },
    // AppBar and Drawer styles are now applied directly in Layout.js
    // Keep these here maybe as fallbacks or remove if Layout.js is always used
    MuiAppBar: {
      styleOverrides: {
        // These styles are overridden by Layout.js currently
        root: {
          background: colors.lightBgAppBar,
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          borderBottom: `1px solid ${colors.subtleBorder}`,
          color: colors.primaryText,
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // These styles are overridden by Layout.js currently
        paper: {
          background: colors.lightBgAppBar,
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          borderRight: `1px solid ${colors.subtleBorder}`,
          borderRadius: '0 16px 16px 0',
          boxShadow: 'none',
          color: colors.primaryText,
        },
      },
    },
    // Update MuiPaper for glassmorphism
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          // Use the semi-transparent background defined in palette
          background: colors.lightBgPaperGlass,
          // Apply backdrop filter
          backdropFilter: 'blur(10px) saturate(160%)',
          WebkitBackdropFilter: 'blur(10px) saturate(160%)',
          borderRadius: '16px',
          border: `1px solid ${colors.lighterBorder}`,
          // Keep existing shadow or adjust if needed
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    // MuiButton styles remain largely the same, but background might interact differently
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '10px',
          border: `1px solid ${colors.subtleBorder}`,
          // Keep existing button background or adjust transparency if needed
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(5px) saturate(150%)',
          WebkitBackdropFilter: 'blur(5px) saturate(150%)',
          color: colors.primaryText,
          padding: '8px 20px',
          textTransform: 'none',
          fontWeight: 600,
          transition:
            'background 0.3s ease, border-color 0.3s ease, transform 0.1s ease',
          '&:hover': {
            background: 'rgba(245, 245, 245, 0.8)',
            borderColor: 'rgba(0, 0, 0, 0.2)',
            transform: 'scale(1.02)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
        // Contained/Outlined/Text variants may need review depending on desired look
        // ... (rest of MuiButton styles) ...
        containedPrimary: {
          border: 'none',
          background: `linear-gradient(to right, ${colors.accentBlue}, #4499FF)`,
          color: '#fff',
          boxShadow: `0 4px 15px rgba(0, 122, 255, 0.2)`,
          '&:hover': {
            background: `linear-gradient(to right, #007AFF, #55AAFF)`,
            boxShadow: `0 6px 20px rgba(0, 122, 255, 0.25)`,
          },
        },
        containedSecondary: {
          border: 'none',
          background: `linear-gradient(to right, ${colors.accentPurple}, #C072EF)`,
          color: '#fff',
          boxShadow: `0 4px 15px rgba(175, 82, 222, 0.2)`,
          '&:hover': {
            background: `linear-gradient(to right, #AF52DE, #D082FF)`,
            boxShadow: `0 6px 20px rgba(175, 82, 222, 0.25)`,
          },
        },
        outlined: {
          border: `1px solid ${colors.subtleBorder}`,
          background: 'transparent',
          backdropFilter: 'none',
          color: colors.secondaryText,
          '&:hover': {
            borderColor: 'rgba(0, 0, 0, 0.2)',
            backgroundColor: colors.hoverBg,
            color: colors.primaryText,
          },
        },
        text: {
          border: 'none',
          background: 'transparent',
          backdropFilter: 'none',
          color: colors.secondaryText,
          '&:hover': {
            backgroundColor: colors.hoverBg,
            color: colors.primaryText,
          },
        },
      },
    },
    // Modal backdrop already has blur, might be fine
    MuiModal: {
      styleOverrides: {
        backdrop: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
      },
    },
    // List items likely don't need backdrop-filter unless placed directly on body
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          margin: '4px 8px',
          padding: '10px 16px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: colors.hoverBg,
          },
          '&.Mui-selected': {
            backgroundColor: colors.selectedBg,
            '&:hover': {
              backgroundColor: colors.selectedHoverBg,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: colors.secondaryText,
          minWidth: '40px',
          '& .Mui-selected &': {
            color: colors.primaryText,
          },
        },
      },
    },
  },
});

export default theme;
